import SEO from "@/components/common/SEO";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageHeader from "@/components/common/PageHeader";
import Footer from "@/components/landing/Footer";
import FloatingActions from "@/components/landing/FloatingActions";
import { ChevronRight } from "lucide-react";
import calculators from "@/components/calculators";
import LoanCalculator from "@/components/calculator/LoanCalculator";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  organizationSchema,
  breadcrumbSchema,
} from "../components/common/schema";

const schemas = [
  organizationSchema(),

  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Loan Calculators",
      path: "/calculators",
    },
  ]),
];

export default function Calculators() {
  const { hash } = useLocation();
  const navigate = useNavigate();

  const hashCalculatorId = hash.replace(/^#/, "");

  const initialCalculator =
    calculators.find((calculator) => calculator.id === hashCalculatorId)?.id ||
    calculators[0]?.id;

  const [activeCalculator, setActiveCalculator] = useState(initialCalculator);

  useEffect(() => {
    const calculatorId = hash.replace(/^#/, "");

    const exists = calculators.some(
      (calculator) => calculator.id === calculatorId,
    );

    if (exists) {
      setActiveCalculator(calculatorId);
    } else if (!hash) {
      setActiveCalculator(calculators[0]?.id);
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const calculator = useMemo(
    () => calculators.find((item) => item.id === activeCalculator),
    [activeCalculator],
  );

  return (
    <>
      <SEO
        title="Loan EMI & Eligibility Calculators"
        description="Calculate your EMI, loan eligibility, affordability and repayment instantly with Money Mart's free loan calculators."
        canonical="/calculators"
        structuredData={schemas}
      />
      <main
        data-testid="calculators-page"
        className="min-h-screen bg-[#F8FAFC]"
      >
        <PageHeader />

        <nav
          aria-label="Breadcrumb"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8"
        >
          <ol className="flex items-center gap-1.5 text-xs text-gray-500">
            <li>
              <Link
                to="/"
                className="font-medium hover:text-[#0F2D52] mm-transition"
              >
                Home
              </Link>
            </li>

            <li aria-hidden="true">
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
            </li>

            <li>
              <span aria-current="page" className="font-medium text-[#0F2D52]">
                Loan Calculators
              </span>
            </li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="pt-8 pb-16 md:pt-10 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-500">
              Free Financial Tools
            </span>
            <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold font-heading tracking-tighter text-[#0F2D52] leading-[1.05] max-w-3xl">
              Loan calculators built for smarter decisions.
            </h1>
            <p className="mt-4 max-w-2xl text-base text-gray-600 leading-relaxed">
              Plan your EMI, check indicative eligibility, understand
              affordability and compare offers — all in seconds. Once
              you&rsquo;re ready, a Money Mart advisor can help you match the
              right lender.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-8">
          <Tabs
            value={activeCalculator}
            onValueChange={(value) => {
              setActiveCalculator(value);
              navigate(`/calculators#${value}`);
            }}
          >
            <div className="overflow-x-auto scrollbar-hide">
              <TabsList className="inline-flex min-w-max gap-1">
                {calculators.map((calculator) => (
                  <TabsTrigger
                    key={calculator.id}
                    value={calculator.id}
                    className="flex items-center gap-2"
                  >
                    {calculator.icon && <calculator.icon className="h-4 w-4" />}

                    <span>{calculator.shortTitle}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </Tabs>

          <LoanCalculator key={calculator.id} config={calculator} />
        </div>

        <Footer />
        <FloatingActions />
      </main>
    </>
  );
}
